import { dashboardApi } from '@/store/api/base/api.base';
import type { TypeNotification } from '@/types/notification/types.notification';

type TypeNotificationPage = {
    items: TypeNotification[];
    unread: number;
    page: { limit: number; count: number; hasMore: boolean; nextCursor: string | null };
};

const EMPTY: TypeNotificationPage = { items: [], unread: 0, page: { limit: 20, count: 0, hasMore: false, nextCursor: null } };

const PAGE = 20;

const asPage = (data: TypeNotificationPage | undefined): TypeNotificationPage => ({
    items: Array.isArray(data?.items) ? data!.items : [],
    unread: Number.isFinite(data?.unread) ? Number(data!.unread) : 0,
    page: data?.page ?? EMPTY.page,
});

export const notificationApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listNotifications: build.query<TypeNotificationPage, void>({
            query: () => ({ url: '/notifications', params: { limit: PAGE } }),
            transformResponse: asPage,
            keepUnusedDataFor: 300,
        }),
        moreNotifications: build.mutation<TypeNotificationPage, string>({
            query: (cursor) => ({ url: '/notifications', params: { limit: PAGE, cursor } }),
            transformResponse: asPage,
            async onQueryStarted(_cursor, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(notificationApi.util.updateQueryData('listNotifications', undefined, (draft) => {
                        for (const item of data.items) if (!draft.items.some((entry) => entry.id === item.id)) draft.items.push(item);
                        draft.page = data.page;
                    }));
                } catch {
                    return;
                }
            },
        }),
        readNotification: build.mutation<TypeNotification, string>({
            query: (id) => ({ url: `/notifications/${encodeURIComponent(id)}/read`, method: 'patch' }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const patch = dispatch(notificationApi.util.updateQueryData('listNotifications', undefined, (draft) => {
                    const item = draft.items.find((entry) => entry.id === id);
                    if (item && !item.read) {
                        item.read = true;
                        item.readAt = new Date().toISOString();
                        draft.unread = Math.max(0, draft.unread - 1);
                    }
                }));
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
        readAllNotifications: build.mutation<{ read: number }, void>({
            query: () => ({ url: '/notifications/read', method: 'patch' }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                const at = new Date().toISOString();
                const patch = dispatch(notificationApi.util.updateQueryData('listNotifications', undefined, (draft) => {
                    for (const item of draft.items) if (!item.read) {
                        item.read = true;
                        item.readAt = at;
                    }
                    draft.unread = 0;
                }));
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
    }),
});

export const { useListNotificationsQuery, useMoreNotificationsMutation, useReadNotificationMutation, useReadAllNotificationsMutation } = notificationApi;
